export interface QueryParamParser<TValue> {
    encode: (value: TValue) => string;
    decode: (value: string) => TValue | undefined;
}

function defineQueryParamParser<TBaseValue>(parser: QueryParamParser<TBaseValue>) {
    return <TValue extends TBaseValue>() => {
        return parser as unknown as QueryParamParser<TValue>;
    };
}

export const stringParam = defineQueryParamParser<string>({
    encode: (value) => value,
    decode: (value) => (value ? value : undefined),
});

export const numberParam = defineQueryParamParser<number>({
    encode: (value) => value.toString(),
    decode: (value) => {
        const parsed = parseFloat(value);
        return isNaN(parsed) ? undefined : parsed;
    },
});

export const booleanParam = defineQueryParamParser<boolean>({
    encode: (value) => value.toString(),
    decode: (value) => {
        switch (value) {
            case 'true':
                return true;
            case 'false':
                return false;
            default:
                return undefined;
        }
    },
});

export const stringArrayParam = defineQueryParamParser<string[]>({
    encode: (value) => value.map(encodeURIComponent).join(','),
    decode: (value) => {
        if (!value) {
            return undefined;
        }
        return value.split(',').map(decodeURIComponent);
    },
});

export const dateParam = defineQueryParamParser<Date>({
    encode: (value) => {
        return value.toISOString();
    },
    decode: (value) => {
        try {
            return new Date(value);
        } catch (e) {
            console.error('Failed to parse query param', e);
            return undefined;
        }
    },
});

export const dateOnlyParam = defineQueryParamParser<Date>({
    encode: (value) => {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    },
    decode: (value) => {
        try {
            return new Date(value);
        } catch (e) {
            console.error('Failed to parse query param', e);
            return undefined;
        }
    },
});

export const jsonParam = defineQueryParamParser<unknown>({
    encode: (value) => JSON.stringify(value),
    decode: (value) => {
        try {
            return JSON.parse(value);
        } catch (e) {
            console.error('Failed to parse query param', e);
            return undefined;
        }
    },
});

export type QueryParamsObjectConfig<T extends object> = {
    [K in keyof T]: QueryParamParser<NonNullable<T[K]>>;
};

export const objectParam = <T extends object>(config: QueryParamsObjectConfig<T>): QueryParamParser<T> => ({
    encode: (values: T) => {
        const result: Partial<Record<keyof T, string>> = {};

        for (const key in config) {
            const parser = config[key] as QueryParamParser<unknown>;
            const value: undefined | unknown = values[key];

            if (value !== undefined && value !== null) {
                result[key] = parser.encode(value);
            }
        }

        return JSON.stringify(result);
    },
    decode: (value) => {
        try {
            const values = JSON.parse(value);
            const result: Partial<T> = {};

            for (const key in config) {
                const parser = config[key] as QueryParamParser<unknown>;

                if (values[key]) {
                    result[key] = parser.decode(values[key]) as never;
                }
            }

            return result as T;
        } catch (e) {
            console.error('Failed to parse query param', e);
            return undefined;
        }
    },
});

export const queryParamsConfig = <T extends object>(config: QueryParamsObjectConfig<T>) => config;

type EnumLike = Record<string, string>;
const enumParserMap = new Map<Record<string, string>, QueryParamParser<string>>();

export const enumParam = <T extends EnumLike>(enumValues: T): QueryParamParser<T[keyof T]> => {
    let parser = enumParserMap.get(enumValues);

    if (!parser) {
        parser = {
            encode: (value) => value,
            decode: (value) => {
                if (!Object.values(enumValues).includes(value)) {
                    return undefined;
                }
                return value;
            },
        };
        enumParserMap.set(enumValues, parser);
    }

    return parser as QueryParamParser<unknown> as QueryParamParser<T[keyof T]>;
};

export const generateQueryParamsString = <T extends object>(
    config: QueryParamsObjectConfig<T>,
    params: Partial<T>,
    urlParams?: URLSearchParams,
): string => {
    const result = new URLSearchParams(urlParams);

    for (const key of Object.keys(config) as Array<keyof T>) {
        const value = params[key];

        if (value === undefined || value === null || value === '') {
            if (result.has(key as string)) {
                result.delete(key as string);
            }
            continue;
        }

        const parser = config[key];

        const encodedValue = parser.encode(value as never);

        if (encodedValue) {
            result.set(key as string, encodedValue);
        } else {
            result.delete(key as string);
        }
    }

    return result.toString();
};
